import { effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import type { RequestStatus } from '@indutrust/shared-model';
import { signalStore, withHooks, withState } from '@ngrx/signals';
import { injectDispatch, on, withReducer } from '@ngrx/signals/events';
import { routerEvents } from './router.events';

/**
 * Slice of state that stores the current router URL string.
 */
export type RouterState = {
  url: string;
  navigationStatus: RequestStatus;
  firstNavigationCompleted: boolean;
};

/**
 * Signal Store that synchronizes the current Angular Router URL into the Signal Store state.
 *
 * Provides `url`, `navigationStatus`, and `firstNavigationCompleted` state,
 * along with methods to update them. Automatically listens to router events
 * and updates the store accordingly.
 *
 * @example
 * const routerStore = inject(RouterStore);
 */
export const RouterStore = signalStore(
  { providedIn: 'root' },
  withState<RouterState>({
    url: location.pathname + location.search + location.hash,
    navigationStatus: 'initial',
    firstNavigationCompleted: false,
  }),
  withReducer(
    on(routerEvents.navigationStarted, () => ({
      navigationStatus: 'loading' as const,
    })),
    on(routerEvents.navigationEnded, ({ payload }) => ({
      navigationStatus: 'success' as const,
      url: payload.url,
    })),
    on(routerEvents.navigationError, ({ payload }) => {
      const error =
        typeof payload === 'object' && 'error' in payload
          ? payload.error
          : String(payload);
      return {
        navigationStatus: { error } as const as RequestStatus,
      };
    }),
    on(routerEvents.firstNavigationCompletedEvent, () => ({
      firstNavigationCompleted: true,
    }))
  ),

  withHooks((store) => {
    const router = inject(Router);
    const dispatch = injectDispatch(routerEvents);

    return {
      onInit() {
        const routerEvents$ = toSignal(router.events);

        effect(() => {
          const routerEvent = routerEvents$();

          if (routerEvent instanceof NavigationStart) {
            dispatch.navigationStarted();
          } else if (routerEvent instanceof NavigationEnd) {
            dispatch.navigationEnded({ url: routerEvent.url });
            if (!store.firstNavigationCompleted()) {
              dispatch.firstNavigationCompletedEvent();
            }
          } else if (routerEvent instanceof NavigationError) {
            dispatch.navigationError({ error: String(routerEvent.error) });
          }
        });
      },
    };
  })
);
